import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link, navigate } from 'gatsby';
import LogoColor from '../../../assets/svg/header/logo-color.svg';
import LogoWhite from '../../../assets/svg/header/logo-white.svg';
import LogoIcon from '../../../assets/svg/header/logo-icon.svg';
import useScrollToFooter from '../../../hooks/useScrollToFooter';
import { useLang } from '../../../context/LanguageContext';
import { t } from '../../../translations';

const HeaderStyled = styled.header`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  z-index: 100;
  width: 100%;
  padding: 20px 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 300ms ease, border-bottom 300ms ease;
  background: ${props => (props.isScrolled || props.isLight) ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  border-bottom: ${props => (props.isScrolled || props.isLight) ? '1px solid #E8E8E8' : '1px solid transparent'};
  backdrop-filter: ${props => (props.isScrolled || props.isLight) ? 'blur(12px)' : 'none'};
  box-shadow: none;
  box-sizing: border-box;

  @media (max-width: 640px) {
    padding: 14px 20px;
  }
`;

const Nav = styled.div`
  display: flex;
  width: 100%;
  max-width: 1280px;
  justify-content: space-between;
  align-items: center;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const FullLogo = styled.img`
  display: block;

  @media (max-width: 640px) {
    height: 28px;
  }
`;

const Menu = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 8px;

  li {
    margin: 0;
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  text-decoration: none;
  border-radius: 6px;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 200ms ease, background 200ms ease;

  &:hover {
    color: #111;
    background: rgba(0,0,0,0.04);
  }

  @media (max-width: 640px) {
    padding: 8px 10px;
    font-size: 13px;
  }
`;

const ContactButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: color 200ms ease, background 200ms ease;

  &:hover {
    color: #111;
    background: rgba(0,0,0,0.04);
  }

  @media (max-width: 640px) {
    padding: 8px 10px;
    font-size: 13px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: background 200ms ease, border-color 200ms ease;

  &:hover {
    background: rgba(0,0,0,0.08);
    border-color: rgba(0,0,0,0.2);
  }

  @media (max-width: 640px) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

const LangToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #AAAAAA;
  background: none;
  border: none;
  cursor: pointer;
  letter-spacing: 0.5px;
  white-space: nowrap;
  transition: color 200ms ease;
  margin-left: 8px;

  span {
    color: #111;
  }

  &:hover {
    color: #888;
  }

  @media (max-width: 640px) {
    padding: 5px 8px;
    font-size: 11px;
    margin-left: 0;
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProjectPage, setIsProjectPage] = useState(false);
  const scrollToFooter = useScrollToFooter();
  const { lang, toggle } = useLang();
  const tr = t[lang].nav;

  const isLightPage = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/project/') ||
    window.location.pathname.startsWith('/work/')
  );

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    setIsProjectPage(window.location.pathname.startsWith('/project/') || window.location.pathname.startsWith('/work/'));
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeaderStyled isScrolled={isScrolled} isLight={isLightPage}>
      <Nav>
        <LogoLink to="/">
          <FullLogo src={LogoColor} height="36" alt="Modernware" />
        </LogoLink>
        <Menu>
          {isProjectPage ? (
            <>
              <li>
                <LangToggle onClick={toggle}>
                  {lang === 'pl' ? <><span>PL</span> / EN</> : <>PL / <span>EN</span></>}
                </LangToggle>
              </li>
              <li>
                <BackButton onClick={() => navigate(-1)}>
                  ← {lang === 'pl' ? 'Powrót' : 'Back'}
                </BackButton>
              </li>
            </>
          ) : (
            <>
              <li>
                <LangToggle onClick={toggle}>
                  {lang === 'pl' ? <><span>PL</span> / EN</> : <>PL / <span>EN</span></>}
                </LangToggle>
              </li>
              <li>
                <MenuButton onClick={scrollToServices}>{tr.services}</MenuButton>
              </li>
              <li>
                <ContactButton onClick={() => scrollToFooter()}>{tr.contact}</ContactButton>
              </li>
            </>
          )}
        </Menu>
      </Nav>
    </HeaderStyled>
  );
};

export default Header;
